import PropTypes from 'prop-types';
import React, { useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
// use @mui
import {
    TableRow,
    TableCell,
    Box,
    Typography,
    MenuItem,
    MenuList,
    ListItemText,
    Divider,
    Button,
    Checkbox,
} from '@mui/material';
// components
import AutocompleteCampaignTags from 'src/components/AutoComplete/AutocompleteCampaignTags';
import { getCsvLink } from '../../../utils/audiencemanagerUtils';
import { Iconify, Label } from '../../../components';
import { TableMoreMenu } from '../../../components/table';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { ConfirmDialog } from '../../../components/dialogPopUp';
// style
import {
    dividerStyle,
    messageTableCellStyle,
    messageTableMenuItem,
} from '../../../styles/MessageListStyle';
import { campaignActionApproval } from '../../../utils/campaignTemplateApproval';

// eslint-disable-next-line no-unused-vars
const CampaignTableRow = ({ cell, row, selected, onSelectRow, onDeleteRow, practiceMessageTable, handleCancelEvent, handleScheduleEvent, handleDuplicateEvent, handleReportEvent, handleCampaignPreview, handlePauseCampaign, handleResumeCampaign, handleAddTag, handleDeleteTag, handleViewLeadsForExisting, onCheckboxToggle }) => {
    const navigate = useNavigate();
    const [openMenu, setOpenMenuActions] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const receipientCountZero = row?.audience?.audience?.recepient_count === 0;
    const sendApproval = campaignActionApproval(row);

    const handleOpenConfirm = () => {
        setOpenConfirm(true);
    };

    const handleCloseConfirm = () => {
        setOpenConfirm(false);
        onDeleteRow();
    };

    const handleCloseMenu = () => {
        setOpenMenuActions(null);
    };

    const handleDuplicateTemplate = () => {
        handleDuplicateEvent(row);
        handleCloseMenu();
    };

    const handleEditItemEvent = () => {
        if (row.status === 'DRAFT') {
            navigate(PATH_DASHBOARD.campaign.builderEdit(row.id));
            handleCloseMenu();
            return;
        }
        onSelectRow(row);
        handleCloseMenu();
    };

    const handleScheduleItemEvent = () => {
        if (!sendApproval.allowed) return;
        handleScheduleEvent(row);
        handleCloseMenu();
    };

    const handlePreviewItemEvent = () => {
        handleCampaignPreview(row);
        setOpenMenuActions(null);
    };

    const handleCancelItemEvent = () => {
        handleCancelEvent(row.id);
        handleCloseMenu();
    };

    const handleReportItemEvent = () => {
        handleReportEvent(row);
        handleCloseMenu();
    };

    const handleDeleteItemEvent = () => {
        handleOpenConfirm();
        handleCloseMenu();
    };

    const handlePauseCampaignRow = () => {
        handlePauseCampaign(row);
        handleCloseMenu();
    }
    const handleResumeCampaignRow = () => {
        if (!sendApproval.allowed) return;
        handleResumeCampaign(row);
        handleCloseMenu();
    }

    const capitalize = rowData => {
        if (!rowData?.length) return "-";
        return `${rowData?.[0]?.toUpperCase()}${rowData?.substring(1)?.toLowerCase()}`
    }

    const getChannelColor = (channel) => {
        const channelColors = { WHATSAPP: 'success', EMAIL: 'info', SMS: 'default' };
        return channelColors[channel] || 'info';
    };

    const getStatusColor = (status) => {
        const statusColors = { DRAFT: 'default', SCHEDULED: 'warning', SENT: 'success', CANCELLED: 'error', SENDING: 'warning', PAUSED: 'info' };
        return statusColors[status] || 'info';
    };

    const handleRowClick = (e) => {
        // Skip navigation if click target is checkbox, action button, menu, or tag input
        const interactive = e.target.closest('button, input, [role="checkbox"], [role="menuitem"], .MuiAutocomplete-root, .MuiIconButton-root');
        if (interactive) return;
        navigate(PATH_DASHBOARD.campaign.report(row.id));
    };

    const cells = {
        'checkbox': (<>

                <Checkbox
                    checked={selected}
                    onClick={(e) => { e.stopPropagation(); onCheckboxToggle(); }}
                />


        </>),
        'name': (<>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(cell && { width: '100%', minWidth: 0 }) }}>
                    <Label
                        variant="ghost"
                        color={getChannelColor(row?.channel)}
                        sx={{ textTransform: 'capitalize', flexShrink: 0 }}
                    >
                        {capitalize(row?.channel)}
                    </Label>
                    <Typography
                        variant='subtitle2'
                        noWrap
                        title={cell ? row?.name : undefined}
                        sx={{ flex: 1, textOverflow: 'ellipsis', ...(cell && { minWidth: 0 }) }}
                    >
                        {row && capitalize(row?.name ?? "")}
                    </Typography>
                </Box>


        </>),
        'status': (<>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Label
                        variant="ghost"
                        color={getStatusColor(row?.status)}
                        sx={{ textTransform: 'capitalize' }}
                    >
                        {row?.status?.toLowerCase?.()}
                    </Label>
                    {(row.status === 'SCHEDULED' || row.status === 'SENDING') && (
                        <Button size="small" variant="outlined" color="warning" onClick={handlePauseCampaignRow} startIcon={<Iconify icon="mdi:pause-circle" width={16} />} sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12, textTransform: 'none' }}>
                            Pause
                        </Button>
                    )}
                    {row.status === 'PAUSED' && (
                        <Button title={sendApproval.reason} disabled={!sendApproval.allowed} size="small" variant="outlined" color="success" onClick={handleResumeCampaignRow} startIcon={<Iconify icon="mdi:play-circle" width={16} />} sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12, textTransform: 'none' }}>
                            Resume
                        </Button>
                    )}
                    {(row.status === 'SCHEDULED' || row.status === 'SENDING' || row.status === 'PAUSED') && (
                        <Button size="small" variant="outlined" color="error" onClick={handleCancelItemEvent} startIcon={<Iconify icon="mdi:cancel" width={16} />} sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12, textTransform: 'none' }}>
                            Stop
                        </Button>
                    )}
                </Box>


        </>),
        'scheduled_at': (<>

                <Box sx={{ whiteSpace: 'nowrap' }}>
                    {moment(row?.scheduled_at, 'DD-MM-YYYY HH:mm:ss').format('ddd, D MMM hh:mm A')}
                </Box>


        </>),
        'tagged_items__tag__name': (<>

                <Box sx={{ whiteSpace: 'nowrap', ...(cell && { width: '100%', minWidth: 0, py: 1 }) }}>
                    <AutocompleteCampaignTags row={row} tagType='campaigns' handleAddTag={handleAddTag} handleDeleteTag={handleDeleteTag} />
                </Box>


        </>),
        '__actions': (<>

                <TableMoreMenu
                    open={openMenu}
                    sx={{ width: '200px' }}
                    onOpen={(event) => setOpenMenuActions(event.currentTarget)}
                    onClose={handleCloseMenu}
                    actions={
                        <MenuList>
                            {row.status === 'SCHEDULED' && <MenuItem onClick={handlePauseCampaignRow}>
                                <Iconify icon={'mdi:pause-circle'} />
                                <ListItemText
                                    primary='Pause'
                                    primaryTypographyProps={messageTableMenuItem}
                                />
                            </MenuItem>}
                            {row.status === 'PAUSED' && <MenuItem title={sendApproval.reason} disabled={!sendApproval.allowed} onClick={handleResumeCampaignRow}>
                                <Iconify icon={'mdi:play-circle'} />
                                <ListItemText
                                    primary='Resume'
                                    primaryTypographyProps={messageTableMenuItem}
                                />
                            </MenuItem>}
                            {(row.status !== 'CANCELLED' && row.status !== 'SENT' && row.status !== 'SCHEDULED' && row.status !== 'SENDING' && row.status !== 'PAUSED') && <>
                                <MenuItem onClick={handleEditItemEvent}>
                                    <Iconify icon={'solar:pen-bold'} />
                                    <ListItemText
                                        primary='Edit'
                                        primaryTypographyProps={messageTableMenuItem}
                                    />
                                </MenuItem>
                                <Divider sx={dividerStyle} />
                            </>}
                            <MenuItem onClick={handleDuplicateTemplate}>
                                <Iconify icon={'bxs:copy'} />
                                <ListItemText
                                    primary='Duplicate'
                                    primaryTypographyProps={messageTableMenuItem}
                                />
                            </MenuItem>
                            <Divider sx={dividerStyle} />
                            {(row.status !== 'CANCELLED' && row.status !== 'SENT' && row.status !== 'SCHEDULED' && row.status !== 'PAUSED') && <>
                                <MenuItem title={sendApproval.reason} disabled={!sendApproval.allowed} onClick={handleScheduleItemEvent}>
                                    <Iconify icon={'material-symbols:schedule'} />
                                    <ListItemText
                                        primary='Schedule'
                                        primaryTypographyProps={messageTableMenuItem}
                                    />
                                </MenuItem>
                                <Divider sx={dividerStyle} />
                            </>}
                            <MenuItem onClick={handlePreviewItemEvent}>
                                <Iconify icon={'icon-park-outline:preview-close'} />
                                <ListItemText
                                    primary='Preview'
                                    primaryTypographyProps={messageTableMenuItem}
                                />
                            </MenuItem>
                            <Divider sx={dividerStyle} />
                            {(row.status !== 'DRAFT' && row.status !== 'CANCELLED' && row.status !== 'SENT' && row.status !== 'PAUSED') && <>
                                <MenuItem onClick={handleCancelItemEvent}>
                                    <Iconify icon={'mdi:cancel'} />
                                    <ListItemText
                                        primary='Cancel'
                                        primaryTypographyProps={messageTableMenuItem}
                                    />
                                </MenuItem>
                                <Divider sx={dividerStyle} />
                            </>}
                            {row.status !== 'DRAFT' && <>
                                <MenuItem onClick={handleReportItemEvent}>
                                    <Iconify icon={'carbon:analytics'} />
                                    <ListItemText
                                        primary='Report'
                                        primaryTypographyProps={messageTableMenuItem}
                                    />
                                </MenuItem>
                                <Divider sx={dividerStyle} />
                                <MenuItem onClick={() => { handleCloseMenu(); navigate(PATH_DASHBOARD.campaign.report(row.id)); }}>
                                    <Iconify icon={'solar:chart-square-bold'} />
                                    <ListItemText
                                        primary='Full Report'
                                        primaryTypographyProps={messageTableMenuItem}
                                    />
                                </MenuItem>
                                <Divider sx={dividerStyle} />
                            </>}
                            {!receipientCountZero && <MenuItem sx={{ color: '#00000' }}
                                onClick={() => {
                                    handleCloseMenu();
                                    handleViewLeadsForExisting(row, getCsvLink(row));
                                }}>
                                <Iconify icon={'mdi:account-view'} sx={{ mr: 0, width: '24px !important', height: '24px !important' }} />
                                <ListItemText primary="Audience list" primaryTypographyProps={messageTableMenuItem} />
                            </MenuItem>}
                            <MenuItem
                                sx={{ color: 'error.main' }}
                                onClick={handleDeleteItemEvent}
                            >
                                <Iconify icon={'solar:trash-bin-trash-bold'} />
                                <ListItemText
                                    primary='Delete'
                                    primaryTypographyProps={messageTableMenuItem}
                                />
                            </MenuItem>
                        </MenuList>
                    }
                />

            <ConfirmDialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                title='Delete'
                content='Are you sure you want to delete this campaign?'
                action={
                    <Button variant='contained' color='error' onClick={handleCloseConfirm}>
                        Delete
                    </Button>
                }
            />
        </>),
    };
    if (cell) return cells[cell] || null;

    return (
        <TableRow hover selected={selected} onClick={handleRowClick} sx={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', '&:hover': { bgcolor: '#f8fafc' } }}>
            {Object.entries(cells).map(([field, content]) => (
                <TableCell key={field} padding={field === 'checkbox' ? 'checkbox' : 'normal'} align={field === '__actions' ? 'right' : 'left'} sx={field === 'tagged_items__tag__name' ? { ...messageTableCellStyle, '&:hover': { border: '1px solid #DCDCDC !important' } } : messageTableCellStyle}>
                    {content}
                </TableCell>
            ))}
        </TableRow>
    );
};

CampaignTableRow.propTypes = {
    cell: PropTypes.string,
    row: PropTypes.object,
    selected: PropTypes.bool,
    onSelectRow: PropTypes.func,
    practiceMessageTable: PropTypes.bool,
    onDeleteRow: PropTypes.func,
    handleCancelEvent: PropTypes.func,
    handleScheduleEvent: PropTypes.func,
    handleDuplicateEvent: PropTypes.func,
    handleReportEvent: PropTypes.func,
    handleCampaignPreview: PropTypes.func,
    handlePauseCampaign: PropTypes.func,
    handleResumeCampaign: PropTypes.func,
    handleAddTag: PropTypes.func,
    handleDeleteTag: PropTypes.func,
    handleViewLeadsForExisting: PropTypes.func,
    onCheckboxToggle: PropTypes.func,
};

export function CampaignTableCell({ field, ...props }) {
    return <CampaignTableRow {...props} cell={field} />;
}
CampaignTableCell.propTypes = { field: PropTypes.string.isRequired };

export default CampaignTableRow;
